// components/Deliveries.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  Plus, Trash2, ArrowLeft, Edit2, Save, X, 
  Users, Calendar, CheckCircle, RefreshCw,
  Truck, Clock 
} from 'lucide-react';
import { useDeliveries } from '../hooks/useDeliveries';
import { useCustomers } from '../hooks/useCustomers';

const Deliveries = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    delivery_date: '',
    cylinder_collector: false
  });
  
  const navigate = useNavigate();
  const { deliveries, loading, addDelivery, updateDelivery, deleteDelivery } = useDeliveries();
  const { customers } = useCustomers();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (!formData.delivery_date) {
      toast.error('Please select delivery date');
      return;
    }
    
    try {
      if (editingId) {
        await updateDelivery(editingId, formData);
      } else {
        await addDelivery(formData);
      }
      
      setShowForm(false);
      setEditingId(null);
      setFormData({ 
        customer_id: '', 
        delivery_date: '', 
        cylinder_collector: false 
      });
    } catch (error) {
      // Error already handled in hook
    }
  };

  const handleEdit = (delivery) => {
    setEditingId(delivery.id);
    setFormData({
      customer_id: delivery.customer_id,
      delivery_date: delivery.delivery_date,
      cylinder_collector: delivery.cylinder_collector
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this delivery?')) {
      await deleteDelivery(id);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ 
      customer_id: '', 
      delivery_date: '', 
      cylinder_collector: false 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading deliveries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navbar */}
      <nav className="bg-gradient-to-r from-purple-700 to-purple-800 text-white p-4 shadow-lg sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center flex-wrap gap-2">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="text-white hover:text-gray-200 transition flex items-center"
            >
              <ArrowLeft className="inline-block mr-2" size={20} />
              Back
            </button>
            <h1 className="text-2xl font-bold">🚚 Manage Deliveries</h1>
          </div>
          <button
            onClick={() => {
              setShowForm(!showForm);
              setEditingId(null);
              setFormData({ 
                customer_id: '', 
                delivery_date: '', 
                cylinder_collector: false 
              });
            }}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md transition flex items-center"
          >
            <Plus className="mr-2" size={18} />
            Add Delivery
          </button>
        </div>
      </nav>

      <div className="container mx-auto p-4 md:p-6">
        {/* Summary Cards */}
        {deliveries.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-purple-500">
              <p className="text-sm text-gray-500 flex items-center gap-1">
                <Truck size={16} /> Total Deliveries
              </p>
              <p className="text-2xl font-bold text-purple-700">{deliveries.length}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-green-500">
              <p className="text-sm text-gray-500 flex items-center gap-1">
                <CheckCircle size={16} /> Collected
              </p>
              <p className="text-2xl font-bold text-green-700">
                {deliveries.filter(d => d.cylinder_collector).length}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-md border-l-4 border-yellow-500">
              <p className="text-sm text-gray-500 flex items-center gap-1">
                <Clock size={16} /> Pending Collection
              </p>
              <p className="text-2xl font-bold text-yellow-700">
                {deliveries.filter(d => !d.cylinder_collector).length}
              </p>
            </div>
          </div>
        )}

        {/* Add/Edit Delivery Form */}
        {showForm && (
          <div className="bg-white p-6 rounded-lg shadow-md mb-6 border-l-4 border-purple-500">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
              {editingId ? (
                <>
                  <Edit2 className="mr-2 text-purple-600" size={20} />
                  Edit Delivery
                </>
              ) : (
                <>
                  <Plus className="mr-2 text-purple-600" size={20} />
                  Add New Delivery
                </>
              )}
            </h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Customer Selection */}
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1 flex items-center gap-1">
                  <Users size={16} className="text-purple-600" />
                  Select Customer <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-800"
                  value={formData.customer_id}
                  onChange={(e) => setFormData({...formData, customer_id: e.target.value})}
                  required
                >
                  <option value="">-- Choose a customer --</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.customer_name} ({c.consumer_number})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">Select the customer for delivery</p>
              </div>

              {/* Delivery Date */}
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-1 flex items-center gap-1">
                  <Calendar size={16} className="text-purple-600" />
                  Delivery Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 text-gray-800"
                  value={formData.delivery_date}
                  onChange={(e) => setFormData({...formData, delivery_date: e.target.value})}
                  required
                />
                <p className="text-xs text-gray-400 mt-1">Select the delivery date</p>
              </div>

              {/* Cylinder Collector Checkbox */}
              <div className="md:col-span-2">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <input
                    type="checkbox"
                    id="collector"
                    className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                    checked={formData.cylinder_collector}
                    onChange={(e) => setFormData({...formData, cylinder_collector: e.target.checked})}
                  />
                  <label htmlFor="collector" className="text-sm text-gray-700 font-medium flex items-center gap-2">
                    <Truck size={18} className="text-purple-600" />
                    Cylinder Collector
                    <span className="text-xs text-gray-400 ml-2">
                      (Check if cylinder was collected)
                    </span>
                  </label>
                </div>
              </div>

              <div className="md:col-span-2 flex gap-4">
                <button 
                  type="submit" 
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-md transition font-semibold flex items-center"
                >
                  <Save className="mr-2" size={18} />
                  {editingId ? 'Update Delivery' : 'Save Delivery'}
                </button>
                <button 
                  type="button" 
                  onClick={handleCancel} 
                  className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-md transition font-semibold flex items-center"
                >
                  <X className="mr-2" size={18} />
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Deliveries Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-purple-700 to-purple-800 text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-sm">#</th>
                  <th className="px-4 py-3 text-left text-sm">👤 Customer</th>
                  <th className="px-4 py-3 text-left text-sm">📅 Delivery Date</th>
                  <th className="px-4 py-3 text-left text-sm">📦 Cylinder Collector</th>
                  <th className="px-4 py-3 text-left text-sm">📊 Status</th>
                  <th className="px-4 py-3 text-left text-sm">⚡ Actions</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No deliveries found. Click "Add Delivery" to add your first delivery!
                    </td>
                  </tr>
                ) : (
                  deliveries.map((delivery, index) => {
                    const isOverdue = new Date(delivery.delivery_date) < new Date();
                    return (
                      <tr key={delivery.id} className="border-b hover:bg-gray-50 transition">
                        <td className="px-4 py-3 text-gray-500 text-sm">{index + 1}</td>
                        <td className="px-4 py-3 text-gray-800 font-medium">{delivery.customer_name}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {delivery.delivery_date}
                          {isOverdue && (
                            <span className="ml-2 text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                              ⚠️ Overdue
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            delivery.cylinder_collector 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {delivery.cylinder_collector ? '✅ Yes' : '❌ No'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                            delivery.cylinder_collector 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-orange-100 text-orange-800'
                          }`}>
                            {delivery.cylinder_collector ? '✅ Collected' : '⏳ Pending'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(delivery)}
                              className="text-purple-600 hover:text-purple-800 transition p-1 hover:bg-purple-50 rounded"
                              title="Edit Delivery"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(delivery.id)}
                              className="text-red-600 hover:text-red-800 transition p-1 hover:bg-red-50 rounded"
                              title="Delete Delivery"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Deliveries;